import * as Sentry from '@sentry/react';
import { logEvent } from 'firebase/analytics';
import mixpanel from 'mixpanel-browser';

import { env } from '@/config/env';
import { isDev, isLocalDev, isStaging } from '@/config/environment';
import { isExcludedEmail } from '@/config/excluded-emails';
import { useAuthStore } from '@/stores/auth-store';
import type { AnalyticsEvent, AnalyticsProperties } from '@/types/analytics';

import { firebaseAnalytics, type FirebaseUser } from './firebase';

const MIXPANEL_API_HOST = 'https://api-eu.mixpanel.com';

const isMixpanelEnabled = !isLocalDev && Boolean(env.MIXPANEL_TOKEN);

export const initAnalytics = () => {
  if (env.SENTRY_DSN) {
    Sentry.init({
      dsn: env.SENTRY_DSN,
      environment: isDev ? 'development' : isStaging ? 'staging' : 'production',
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
    });
  }

  if (!isLocalDev && env.MIXPANEL_TOKEN) {
    mixpanel.init(env.MIXPANEL_TOKEN, {
      api_host: MIXPANEL_API_HOST,
      debug: false,
      persistence: 'localStorage',
      track_pageview: false,
    });
  }
};

const isTrackingDisabled = () => {
  if (isLocalDev) return true;
  const email = useAuthStore.getState().firebaseUser?.email ?? '';
  return isExcludedEmail(email);
};

const safely = (emit: () => void) => {
  try {
    emit();
  } catch (error) {
    Sentry.captureException(error);
  }
};

type SanitizedProperties = Record<string, string | number | boolean>;

const sanitizeProperties = (
  properties?: AnalyticsProperties,
): SanitizedProperties =>
  Object.fromEntries(
    Object.entries(properties ?? {}).filter(
      (entry): entry is [string, string | number | boolean] =>
        entry[1] !== undefined && entry[1] !== null,
    ),
  );

export const identifyUser = (user: FirebaseUser) => {
  if (!isMixpanelEnabled) return;
  if (isExcludedEmail(user.email ?? '')) return;

  safely(() => {
    mixpanel.identify(user.uid);
    mixpanel.people.set({
      $name: user.displayName ?? '',
      $email: user.email,
    });
  });
};

export const setUserProperties = (properties: SanitizedProperties) => {
  if (!isMixpanelEnabled || isTrackingDisabled()) return;
  safely(() => mixpanel.people.set(properties));
};

export const registerSuperProperties = (properties: AnalyticsProperties) => {
  if (!isMixpanelEnabled || isTrackingDisabled()) return;
  safely(() => mixpanel.register(sanitizeProperties(properties)));
};

export const resetAnalytics = () => {
  if (!isMixpanelEnabled) return;
  safely(() => mixpanel.reset());
};

export const trackEvent = (
  event: AnalyticsEvent,
  properties?: AnalyticsProperties,
) => {
  if (isTrackingDisabled()) return;

  const params = sanitizeProperties(properties);
  if (isMixpanelEnabled) safely(() => mixpanel.track(event, params));
  safely(() => logEvent(firebaseAnalytics, event, params));
};

export { Sentry, mixpanel };
